import React from 'react';
import { DropdownOptionType, OptionsAlignmentTypes } from '@insurancemenu/imenu-components-lib/dist/types/Dropdown';
import { CloudUpload, Delete, Mail, Edit, UserGroup, Reload } from '@insurancemenu/imenu-components-lib/dist/icons';
import { observer } from 'mobx-react-lite';
import ActionsDropdown from 'components/common/general/ActionsDropdown';
import { useTriggerRenewalsPageStore } from 'store/pages/tools/trigger-renewals/pageStore';
import { EnrollmentDelegate } from 'store/pages/tools/trigger-renewals/enrollmentDelegate';
import { BiGroup } from 'types/groupService';
import { useRootStore } from 'store/rootStore';
import { imCelticsRights } from '@insurancemenu/imenu-components-lib/dist/utils/accessRights';

interface Props {
  group: BiGroup;
  currentEnrollmentDelegate: EnrollmentDelegate;
}

const MoreDropdown: React.FC<Props> = observer(({ group, currentEnrollmentDelegate }) => {
  const {
    userStore: { userRoles },
  } = useRootStore();
  const { reloginToGroupProfile, renewalGroupActionDelegate } = useTriggerRenewalsPageStore();

  const onViewGroupProfile = async (): Promise<void> => {
    await reloginToGroupProfile(group.id, group.agency.id, group.agency.name);
  };

  const setActionContext = (): void => {
    renewalGroupActionDelegate.setActiveContext({ enrollmentDelegate: currentEnrollmentDelegate, group });
  };

  const convertedUserRoles = userRoles.slice();
  const { moveToRenewal, manageEmails, manageExhibits, changeEffectiveDate } =
    currentEnrollmentDelegate.enrollmentActions;

  const viewGroupProfileOption: DropdownOptionType = {
    value: 'viewGroupProfile',
    icon: UserGroup,
    label: 'View Group Profile',
    onOptionClick: onViewGroupProfile,
  };

  const moveToRenewalOption: DropdownOptionType = {
    value: 'moveToRenewal',
    icon: Reload,
    label: 'Move to Renewal',
    disabled: moveToRenewal.isDisabled,
    onOptionClick: (): void => {
      setActionContext();
      renewalGroupActionDelegate.openMoveToRenewal();
    },
  };

  const manageEmailsOption: DropdownOptionType = {
    value: 'manageEmails',
    icon: Mail,
    label: 'Manage Emails',
    disabled: manageEmails.isDisabled,
    onOptionClick: (): void => {
      setActionContext();
      renewalGroupActionDelegate.openManageEmails();
    },
  };

  const manageExhibitOption: DropdownOptionType = {
    value: 'manageExhibit',
    icon: CloudUpload,
    label: 'Manage Exhibit',
    disabled: manageExhibits.isDisabled,
    onOptionClick: (): void => {
      setActionContext();
      renewalGroupActionDelegate.openManageExhibits();
    },
  };

  const editEffectiveDateOption: DropdownOptionType = {
    value: 'editEffectiveDate',
    icon: Edit,
    label: 'Change Effective Date',
    disabled: changeEffectiveDate.isDisabled,
    onOptionClick: (): void => {
      setActionContext();
      renewalGroupActionDelegate.openChangeEffectiveDate();
    },
  };

  const removePeriodOption: DropdownOptionType = {
    value: 'removePeriod',
    icon: Delete,
    label: 'Remove Period',
    customColor: 'red',
    onOptionClick: (): void => {
      setActionContext();
      renewalGroupActionDelegate.openRemovePeriod();
    },
  };

  const dropdownOptions: DropdownOptionType[] = [
    viewGroupProfileOption,
    ...(moveToRenewal.isVisible ? [moveToRenewalOption] : []),
    ...(manageEmails.isVisible ? [manageEmailsOption] : []),
    ...(manageExhibits.isVisible ? [manageExhibitOption] : []),
    ...(changeEffectiveDate.isVisible ? [editEffectiveDateOption] : []),
    ...(imCelticsRights(convertedUserRoles) ? [removePeriodOption] : []),
  ];

  return (
    <ActionsDropdown
      name="more"
      options={dropdownOptions}
      optionsAlignment={OptionsAlignmentTypes.BOTTOM_LEFT}
      optionsWidth="22rem"
    />
  );
});

export default MoreDropdown;
